/**
 * src/pages/GlossaryAndReferences/GlossaryAndReferences.tsx
 * Page entry point for "Glossary & References" (Area 5/6 cross-cutting foundation).
 *
 * Implements:
 * - KnowledgePageShell with pageId="glossary", character="glossary", accent="cyan"
 * - PageHero with title, subtitle, and telemetry pill
 * - HowToUseSection explaining navigation and fictional examples vs real sources
 * - GlossarySection with live search, A-Z jump strip, category filter, in-page anchors, and target highlighting
 * - ReferencesSection with 3-tier reading library (introductory, intermediate, advanced) and primary/secondary flags
 * - StatusKey showing epistemic badges
 * - RelatedLinks routing out to all other Architecture of Knowledge modules and Simulation Theory
 * - Mobile responsive design (320px+ compatible)
 */

import React from "react";
import { KnowledgePageShell } from "@/components/knowledge/KnowledgePageShell";
import { PageHero } from "@/components/knowledge/PageHero";
import { StatusKey } from "@/components/knowledge/StatusKey";
import { RelatedLinks } from "@/components/knowledge/RelatedLinks";
import { HowToUseSection } from "./sections/HowToUseSection";
import { GlossarySection } from "./sections/GlossarySection";
import { ReferencesSection } from "./sections/ReferencesSection";
import { BookOpen, Library, Hash, ArrowDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import "./glossaryReferences.css";
export default function GlossaryAndReferencesPage() {
  return <KnowledgePageShell pageId="glossary" character="glossary" accent="cyan">
      {/* Hero Banner */}
      <PageHero eyebrow="Master Index & Citation Library" title="Glossary & References" subtitle="The definitive lexicon of rational inquiry. Over 70 rigorous definitions across propositional logic, epistemology, cognitive science, and scientific demarcation, paired with a curated three-tier academic reading library.">
        <div className="ql-glass p-5 rounded-xl border border-border/60 flex flex-col gap-3.5 text-xs font-mono">
          <div className="flex items-center justify-between pb-2 border-b border-border/40">
            <span className="text-muted-foreground">Index Volume</span>
            <span className="text-primary font-bold">70+ Formally Defined Terms</span>
          </div>
          <div className="flex items-center justify-between pb-2 border-b border-border/40">
            <span className="text-muted-foreground">Reading Library</span>
            <span className="text-foreground font-bold">3 Tiers · Primary & Secondary</span>
          </div>
          <div className="flex items-center justify-between pb-2 border-b border-border/40">
            <span className="text-muted-foreground">Epistemic Grading</span>
            <span className="text-primary font-bold">9-Tier Status Validation</span>
          </div>
          <div className="pt-1 flex gap-2">
            <Link to="#glossary-archive" className="ql-btn-neon text-xs py-2 px-3.5 flex-1 text-center inline-flex items-center justify-center gap-1.5">
              <span>Explore Glossary</span>
              <ArrowDown className="size-3" aria-hidden="true" />
            </Link>
            <Link to="#reading-library" className="ql-glass py-2 px-3.5 flex-1 text-center inline-flex items-center justify-center gap-1.5 rounded-full border border-border hover:border-primary text-foreground text-xs">
              <span>Reading Library</span>
            </Link>
          </div>
        </div>
      </PageHero>

      {/* Guide: How to use, anchors, and fictional vs real sources */}
      <HowToUseSection />

      {/* Interactive Master Glossary */}
      <GlossarySection />

      {/* Status Classification Key */}
      <div className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 py-8 min-w-0">
        <StatusKey />
      </div>

      {/* Academic Citation & Reading Library */}
      <ReferencesSection />

      {/* Cross-Page Related Links */}
      <div className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 py-8 min-w-0">
        <RelatedLinks title="Explore Connected Laboratories & Workshops" items={[{
        to: "/LogicLaboratoryPage",
        label: "Logic Laboratory",
        why: "Test formal validity, build propositional truth tables, and examine syllogistic deductions in an interactive proof simulator."
      }, {
        to: "/CriticalThinkingPage",
        label: "Critical Thinking Workshop",
        why: "Deconstruct arguments into premises, practice the principle of charity, and steelman counter-arguments."
      }, {
        to: "/FallacyLibraryPage",
        label: "Fallacy Library",
        why: "Examine interactive specimens of formal, informal, and probabilistic fallacies with active diagnostic tests."
      }, {
        to: "/KnowledgeLaboratoryPage",
        label: "Knowledge Laboratory",
        why: "Investigate theories of justification, examine Gettier counterexamples, and test foundationalism vs coherentism."
      }, {
        to: "/PhilosophyAsInvestigationPage",
        label: "Philosophy as Investigation",
        why: "Explore conceptual analysis, thought experiments, and reflective equilibrium as rigorous investigative tools."
      }, {
        to: "/SimulationTheoryPage",
        label: "Simulation Theory",
        why: "Apply epistemological skepticism, probability theory, and physicalist models to Nick Bostrom's simulation trilemma."
      }]} />
      </div>
    </KnowledgePageShell>;
}